import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiEdit, FiTrash2, FiCheckCircle, FiArchive, FiLock } from 'react-icons/fi';
import baselineService from '../../services/baselineService';
import './BaselineView.css';

const BaselineView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [baseline, setBaseline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBaseline();
  }, [id]);

  const fetchBaseline = async () => {
    try {
      setLoading(true);
      const data = await baselineService.getBaselineById(id);
      setBaseline(data.baseline);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch baseline');
      console.error('Error fetching baseline:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this baseline?')) {
      try {
        await baselineService.deleteBaseline(id);
        alert('Baseline deleted successfully!');
        navigate('/app/baselines');
      } catch (err) {
        alert(err.message || 'Failed to delete baseline');
      }
    }
  };

  const handleApprove = async () => {
    if (window.confirm('Are you sure you want to approve this baseline? It will be locked after approval.')) {
      try {
        await baselineService.approveBaseline(id);
        alert('Baseline approved successfully!');
        fetchBaseline();
      } catch (err) {
        alert(err.message || 'Failed to approve baseline');
      }
    }
  };

  const handleArchive = async () => {
    if (window.confirm('Are you sure you want to archive this baseline?')) {
      try {
        await baselineService.archiveBaseline(id);
        alert('Baseline archived successfully!');
        fetchBaseline();
      } catch (err) {
        alert(err.message || 'Failed to archive baseline');
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      'Draft': 'badge-warning',
      'Active': 'badge-success',
      'Archived': 'badge-secondary',
      'Superseded': 'badge-info'
    };
    return statusClasses[status] || 'badge-secondary';
  };

  if (loading) {
    return (
      <div className="baseline-view-loading">
        <div className="spinner"></div>
        <p>Loading baseline...</p>
      </div>
    );
  }

  if (error || !baseline) {
    return (
      <div className="baseline-view-error">
        <p>{error || 'Baseline not found'}</p>
        <button onClick={() => navigate('/app/baselines')} className="btn btn-primary">
          Back to Baselines
        </button>
      </div>
    );
  }

  return (
    <div className="baseline-view-page">
      <div className="page-header">
        <button onClick={() => navigate('/app/baselines')} className="back-button">
          <FiArrowLeft /> Back to Baselines
        </button>

        <div className="header-content">
          <div className="title-section">
            <h1>
              {baseline.is_locked && <FiLock className="lock-icon" title="Locked" />}
              {baseline.name}
            </h1>
            <div className="badges">
              <span className={`badge ${getStatusBadgeClass(baseline.status)}`}>
                {baseline.status}
              </span>
              <span className="badge badge-primary">{baseline.baseline_type}</span>
              <span className="version-badge">v{baseline.version}</span>
            </div>
          </div>

          <div className="header-actions">
            {!baseline.is_locked && baseline.status === 'Draft' && (
              <>
                <Link to={`/app/baselines/${id}/edit`} className="btn btn-primary">
                  <FiEdit /> Edit
                </Link>
                <button onClick={handleApprove} className="btn btn-success">
                  <FiCheckCircle /> Approve
                </button>
              </>
            )}
            {baseline.status === 'Active' && (
              <button onClick={handleArchive} className="btn btn-warning">
                <FiArchive /> Archive
              </button>
            )}
            {!baseline.is_locked && baseline.status !== 'Active' && (
              <button onClick={handleDelete} className="btn btn-danger">
                <FiTrash2 /> Delete
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="baseline-content">
        <div className="sections-row">
        {/* Basic Information */}
        <div className="info-section">
          <h2>Basic Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Project</label>
              <div>
                {baseline.project ? (
                  <Link to={`/app/projects/${baseline.project.id}`}>
                    {baseline.project.name}
                  </Link>
                ) : (
                  'N/A'
                )}
              </div>
            </div>
            <div className="info-item">
              <label>Baseline Date</label>
              <div>{formatDate(baseline.baseline_date)}</div>
            </div>
            <div className="info-item">
              <label>Created By</label>
              <div>
                {baseline.creator
                  ? `${baseline.creator.first_name} ${baseline.creator.last_name}`
                  : 'N/A'}
              </div>
            </div>
            <div className="info-item">
              <label>Approved By</label>
              <div>
                {baseline.approver
                  ? `${baseline.approver.first_name} ${baseline.approver.last_name}`
                  : 'Not approved'}
              </div>
            </div>
          </div>
          {baseline.description && (
            <div className="info-item full-width">
              <label>Description</label>
              <div>{baseline.description}</div>
            </div>
          )}
        </div>

        {/* Scope Baseline */}
        <div className="info-section">
          <h2>Scope Baseline</h2>
          {baseline.scope_description && (
            <div className="info-item">
              <label>Scope Description</label>
              <div className="text-content">{baseline.scope_description}</div>
            </div>
          )}
          {baseline.deliverables && baseline.deliverables.length > 0 && (
            <div className="info-item">
              <label>Deliverables</label>
              <ul className="list-content">
                {Array.isArray(baseline.deliverables)
                  ? baseline.deliverables.map((item, index) => (
                    <li key={index}>{item}</li>
                  )) : null}
              </ul>
            </div>
          )}

          {baseline.acceptance_criteria && (
            <div className="info-item">
              <label>Acceptance Criteria</label>
              <div className="text-content">{baseline.acceptance_criteria}</div>
            </div>
          )}
        </div>
        </div>
        
 <div className="sections-row">
        {/* Schedule Baseline */}
        <div className="info-section" style={{height:"850px"}}>
          <h2>Schedule Baseline</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Planned Start Date</label>
              <div>{formatDate(baseline.planned_start_date)}</div>
            </div>
            <div className="info-item">
              <label>Planned End Date</label>
              <div>{formatDate(baseline.planned_end_date)}</div>
            </div>
            <div className="info-item">
              <label>Duration</label>
              <div>{baseline.planned_duration_days ? `${baseline.planned_duration_days} days` : 'N/A'}</div>
            </div>
          </div>
          {baseline.milestones && baseline.milestones.length > 0 && (
            <div className="info-item">
              <label>Milestones</label>
              <ul className="list-content">
                {Array.isArray(baseline.milestones)
                  ?  baseline.milestones.map((item, index) => (
                  <li key={index}>
                    <strong>{item.name}</strong> - {formatDate(item.date)}
                  </li>
                )) : null}
              </ul>
            </div>
          )}
        </div>

        {/* Cost Baseline */}
<div className="info-section">
  <h2>Cost Baseline</h2>
  <div className="info-grid">
    <div className="info-item highlight">
      <label>Total Planned Budget</label>
      <div>{formatCurrency(baseline.planned_budget)}</div>
    </div>
    <div className="info-item">
      <label>Labor Cost</label>
      <div>{formatCurrency(baseline.planned_labor_cost)}</div>
    </div>
    <div className="info-item">
      <label>Material Cost</label>
      <div>{formatCurrency(baseline.planned_material_cost)}</div>
    </div>
    <div className="info-item">
      <label>Equipment Cost</label>
      <div>{formatCurrency(baseline.planned_equipment_cost)}</div>
    </div>
    <div className="info-item">
      <label>Other Cost</label>
      <div>{formatCurrency(baseline.planned_other_cost)}</div>
    </div>
    <div className="info-item">
      <label>Contingency Reserve</label>
      <div>{formatCurrency(baseline.contingency_reserve)}</div>
    </div>
    <div className="info-item">
      <label>Management Reserve</label>
      <div>{formatCurrency(baseline.management_reserve)}</div>
    </div>
  </div>
</div>
       </div>
        {/* Additional Information */}
        {(baseline.assumptions || baseline.constraints || baseline.notes) && (
          <div className="info-section">
            <h2>Additional Information</h2>
            {baseline.assumptions && (
              <div className="info-item">
                <label>Assumptions</label>
                <div className="text-content">{baseline.assumptions}</div>
              </div>
            )}
            {baseline.constraints && (
              <div className="info-item">
                <label>Constraints</label>
                <div className="text-content">{baseline.constraints}</div>
              </div>
            )}
            {baseline.notes && (
              <div className="info-item">
                <label>Notes</label>
                <div className="text-content">{baseline.notes}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BaselineView;


